import React, {useCallback, useEffect, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    Collapse,
    Divider,
    IconButton,
    Modal,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined"
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import ShuffleIcon from "@mui/icons-material/Shuffle"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import useSaveShortcut from "../../hooks/useSaveShortcut"
import ApiError from "../../api/ApiError"
import AsyncButton from "../button/AsyncButton"
import ErrorMessage from "../error/ErrorMessage"
import ColumnReferenceTextField from "../templates/ColumnReferenceTextField"
import TemplateUtil from "../../library/TemplateUtil"
import {useProjectData} from "../../hooks/useProjectData"
import {TransformRequests} from "../../api/requests/TransformRequests"
import TransformJson, {AnyTransformJson} from "../../api/json/TransformJson"
import TransformJobJson from "../../api/json/TransformJobJson"
import TransformJobForm from "./TransformJobForm"
import TransformQualityCheckForm from "./TransformQualityCheckForm"
import RowJson from "../../api/json/RowJson"
import {RowRequests} from "../../api/requests/RowRequests"
import {CompletionOptionsJson, DEFAULT_COMPLETION_OPTIONS} from "../../api/json/CompletionOptionsJson"
import {CompletionResponseJson} from "../../api/json/CompletionResponseJson"
import {AnyModelSpecJson} from "../../api/json/ModelSpecJson"
import CompletionOptionsFormModal from "../completions/CompletionOptionsFormModal"

interface TransformEditModalProps {
    open: boolean
    onClose: () => void
    transformId?: string
    onCreated?: (transform: TransformJson) => void
    onChanged?: (transform: TransformJson) => void
    onDeleted?: (transformId: string) => void
    onJobStarted?: (job: TransformJobJson, transform: TransformJson) => void
}

const EMPTY_DRAFT: AnyTransformJson = {
    name: "Untitled",
    systemPrompt: "",
    userPrompt: "",
}

function TransformEditModal({
                                open,
                                onClose,
                                transformId,
                                onCreated,
                                onChanged,
                                onJobStarted,
                            }: TransformEditModalProps) {
    const {id: projectId, project} = useProjectData()

    const [savedTransform, setSavedTransform] = useState<TransformJson>()
    const [editingTransform, setEditingTransform] = useState<AnyTransformJson>(EMPTY_DRAFT)

    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState<ApiError>()

    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState<ApiError>()

    const [activeSection, setActiveSection] = useState<"system" | "user" | null>(null)
    const [runExpanded, setRunExpanded] = useState(false)
    const [testExpanded, setTestExpanded] = useState(false)
    const [qualityCheckExpanded, setQualityCheckExpanded] = useState(false)

    // Test-on-a-row state
    const [testRow, setTestRow] = useState<RowJson>()
    const [loadingRow, setLoadingRow] = useState(false)
    const [testModel, setTestModel] = useState<AnyModelSpecJson>()
    const [testCompletionOptions, setTestCompletionOptions] = useState<CompletionOptionsJson>({
        ...DEFAULT_COMPLETION_OPTIONS,
        numberOfCompletions: 1,
    })
    const [testSettingsOpen, setTestSettingsOpen] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [completionResponse, setCompletionResponse] = useState<CompletionResponseJson>()
    const [completionError, setCompletionError] = useState<ApiError>()

    // Load existing transform whenever the modal opens with an id; reset when it opens in create mode.
    useEffect(() => {
        if (!open) return
        setSaveError(undefined)
        setLoadError(undefined)
        setCompletionResponse(undefined)
        setCompletionError(undefined)
        setRunExpanded(false)

        if (!transformId || !projectId) {
            setSavedTransform(undefined)
            setEditingTransform(EMPTY_DRAFT)
            return
        }

        setLoading(true)
        TransformRequests.get(projectId, transformId)
            .then(loaded => {
                setSavedTransform(loaded)
                setEditingTransform(loaded)
            })
            .catch(setLoadError)
            .finally(() => setLoading(false))
    }, [open, transformId, projectId])

    // Preload a random test row when the modal opens on a saved transform.
    useEffect(() => {
        if (!open || !projectId || !savedTransform || testRow) return
        loadRandomRow()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId, savedTransform])

    const loadRandomRow = () => {
        if (!projectId || loadingRow) return
        setLoadingRow(true)
        RowRequests.getRandom(projectId, 1)
            .then(page => {
                if (page.content.length > 0) {
                    setTestRow(page.content[0])
                    setCompletionResponse(undefined)
                    setCompletionError(undefined)
                }
            })
            .catch(() => {/* silent */
            })
            .finally(() => setLoadingRow(false))
    }

    // --- Derived values + hook handlers must run on every render (not after early returns). ---

    const unsavedChanges = !savedTransform
        || savedTransform.name !== editingTransform.name
        || savedTransform.systemPrompt !== editingTransform.systemPrompt
        || savedTransform.userPrompt !== editingTransform.userPrompt

    const handleSave = useCallback(() => {
        if (!projectId) return
        if (!unsavedChanges) return
        setSaving(true)
        setSaveError(undefined)

        if (savedTransform) {
            TransformRequests.update(
                projectId,
                savedTransform.id,
                editingTransform.name,
                editingTransform.systemPrompt,
                editingTransform.userPrompt,
            )
                .then(updated => {
                    setSavedTransform(updated)
                    setEditingTransform(updated)
                    onChanged && onChanged(updated)
                })
                .catch(setSaveError)
                .finally(() => setSaving(false))
        } else {
            TransformRequests.create(projectId, editingTransform.systemPrompt, editingTransform.userPrompt)
                .then(created => {
                    setSavedTransform(created)
                    setEditingTransform(created)
                    onCreated && onCreated(created)
                })
                .catch(setSaveError)
                .finally(() => setSaving(false))
        }
    }, [projectId, savedTransform, editingTransform, unsavedChanges, onChanged, onCreated])

    // Cmd/Ctrl+S saves when the modal is open and there are unsaved changes.
    // Must be declared before any early return to comply with React's rules of hooks.
    useSaveShortcut(() => {
        if (open) handleSave()
    })

    if (!project) {
        return <></>
    }

    const usedReferences = TemplateUtil.extractReferencesFromString(
        editingTransform.systemPrompt + editingTransform.userPrompt,
    )
    const unreferencedFields = project.columns.filter(f => usedReferences.indexOf(f.reference) === -1)

    const hasActiveJob = savedTransform?.hasActiveJob ?? false

    const updateField = (patch: Partial<AnyTransformJson>) => {
        setEditingTransform(prev => ({...prev, ...patch}))
    }

    const handleTestGenerate = () => {
        if (!projectId || !savedTransform || !testRow || !testModel || !testCompletionOptions) return
        setGenerating(true)
        setCompletionError(undefined)
        const inputs: Record<string, string> = {}
        testRow.cells.forEach(cell => {
            inputs[cell.column.reference] = cell.value
        })
        TransformRequests.complete(
            projectId,
            savedTransform.id,
            testModel.platform,
            testModel.name,
            inputs,
            testCompletionOptions,
        )
            .then(response => setCompletionResponse(response))
            .catch(setCompletionError)
            .finally(() => setGenerating(false))
    }

    const handleJobStarted = (newJob: TransformJobJson) => {
        if (savedTransform) {
            const updated: TransformJson = {
                ...savedTransform,
                hasActiveJob: true,
                transformJobs: [newJob, ...savedTransform.transformJobs],
            }
            setSavedTransform(updated)
            onChanged && onChanged(updated)
            onJobStarted && onJobStarted(newJob, updated)
        }
        onClose()
    }

    const canRun = !!savedTransform && !unsavedChanges && !hasActiveJob
    const testDisabledTooltip = !savedTransform
        ? "Save the transform first."
        : unsavedChanges
            ? "Save your changes first."
            : !testModel
                ? "Pick a model in settings."
                : !testRow
                    ? "No sample row available."
                    : ""

    return (
        <>
            <Modal open={open} onClose={(_, reason) => {
                if (reason !== "backdropClick" || !unsavedChanges) onClose()
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '92vw',
                    maxWidth: 760,
                    maxHeight: '90vh',
                    backgroundColor: '#f7f7f7',
                    borderRadius: '6px',
                    outline: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <Stack direction="row"
                           alignItems="center"
                           justifyContent="space-between"
                           sx={{px: 3, py: 2, backgroundColor: '#fff'}}>
                        <Typography variant="h3" fontSize={18} fontWeight={500} sx={{color: '#555'}}>
                            {savedTransform ? editingTransform.name || "Transform" : "New Transform"}
                        </Typography>
                        {!(!!editingTransform.systemPrompt?.trim() || activeSection === "system") &&
                            <Button size="small"
                                    onClick={() => setActiveSection("system")}>
                                Add system prompt
                            </Button>
                        }
                    </Stack>
                    <Divider/>

                    <Box sx={{px: 3, py: 3, overflowY: 'auto', flex: '1 1 auto'}}>
                        {loadError && <ErrorMessage error={loadError}/>}
                        {loading && !savedTransform &&
                            <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        }

                        {!loading && (
                            <Stack spacing={1}>
                                <TextField label="Name"
                                           value={editingTransform.name ?? ""}
                                           onChange={(e) => updateField({name: e.target.value})}
                                />

                                {(!!editingTransform.systemPrompt?.trim() || activeSection === "system") &&
                                    <ColumnReferenceTextField label="System Prompt"
                                                              value={editingTransform.systemPrompt}
                                                              unreferencedFields={unreferencedFields}
                                                              setValue={(newValue) => updateField({systemPrompt: newValue})}
                                                              showInsertButtons={activeSection === "system"}
                                                              didFocus={() => setActiveSection("system")}/>
                                }
                                <ColumnReferenceTextField label="Instructions"
                                                          value={editingTransform.userPrompt}
                                                          unreferencedFields={unreferencedFields}
                                                          setValue={(newValue) => updateField({userPrompt: newValue})}
                                                          showInsertButtons={activeSection === "user"}
                                                          didFocus={() => setActiveSection("user")}/>

                                {unsavedChanges && hasActiveJob &&
                                    <Alert severity="warning">
                                        Making changes while there is an active transform job
                                        will affect all remaining outputs for the job.
                                    </Alert>
                                }

                                {saveError && <ErrorMessage error={saveError}/>}

                                {/* Test section — collapsible, so the modal stays short while editing. */}
                                {savedTransform &&
                                    <Box sx={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #ececec',
                                        borderRadius: 1,
                                    }}>
                                        <Stack direction="row"
                                               alignItems="center"
                                               justifyContent="space-between"
                                               onClick={() => setTestExpanded(e => !e)}
                                               sx={{
                                                   px: 2,
                                                   py: 1.5,
                                                   cursor: 'pointer',
                                                   userSelect: 'none',
                                               }}>
                                            <Typography variant="subtitle2">Try it on a sample row</Typography>
                                            <IconButton size="small" sx={{pointerEvents: 'none'}}>
                                                {testExpanded ? <ExpandLessIcon fontSize="small"/> :
                                                    <ExpandMoreIcon fontSize="small"/>}
                                            </IconButton>
                                        </Stack>
                                        <Collapse in={testExpanded} unmountOnExit>
                                            <Box sx={{px: 2, pb: 2}}>
                                                {!testRow &&
                                                    <Typography variant="caption" color="text.secondary">
                                                        {loadingRow ? "Loading a sample row…" : "No sample row available."}
                                                    </Typography>
                                                }

                                                {testRow &&
                                                    <Stack spacing={1.5}>
                                                        <Box sx={{
                                                            backgroundColor: '#fafafa',
                                                            border: '1px solid #f0f0f0',
                                                            borderRadius: 1,
                                                            px: 1.5,
                                                            py: 1,
                                                            whiteSpace: 'pre-wrap',
                                                            fontSize: 13,
                                                            color: 'text.secondary',
                                                            maxHeight: 200,
                                                            overflowY: 'auto',
                                                        }}>
                                                            {savedTransform.systemPrompt &&
                                                                <Box sx={{mb: 1}}>
                                                                    {TemplateUtil.renderAny(
                                                                        savedTransform.systemPrompt,
                                                                        testRow.cells,
                                                                        project.columns,
                                                                    )}
                                                                </Box>
                                                            }
                                                            {TemplateUtil.renderAny(
                                                                savedTransform.userPrompt,
                                                                testRow.cells,
                                                                project.columns,
                                                            )}
                                                        </Box>

                                                        <Stack direction="row"
                                                               spacing={1}
                                                               alignItems="center"
                                                               justifyContent="space-between">
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <AsyncButton variant="outlined"
                                                                             color="secondary"
                                                                             size="small"
                                                                             endIcon={<BoltOutlinedIcon/>}
                                                                             waiting={generating}
                                                                             disabled={!!testDisabledTooltip}
                                                                             tooltip={testDisabledTooltip || undefined}
                                                                             onClick={handleTestGenerate}>
                                                                    Run test
                                                                </AsyncButton>
                                                                {testModel &&
                                                                    <Typography variant="caption"
                                                                                color="text.secondary">
                                                                        {testModel.name}
                                                                    </Typography>
                                                                }
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Temp {testCompletionOptions.temperature}
                                                                </Typography>
                                                            </Stack>
                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                <Button size="small"
                                                                        color="secondary"
                                                                        startIcon={<ShuffleIcon fontSize="small"/>}
                                                                        onClick={loadRandomRow}
                                                                        disabled={loadingRow}>
                                                                    New sample
                                                                </Button>
                                                                <IconButton size="small"
                                                                            onClick={() => setTestSettingsOpen(true)}>
                                                                    <SettingsOutlinedIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Stack>
                                                        </Stack>

                                                        {completionError && <ErrorMessage error={completionError}/>}

                                                        {!completionResponse && !completionError && !generating &&
                                                            <Typography variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{fontStyle: 'italic'}}>
                                                                Click Run test to see the output.
                                                            </Typography>
                                                        }

                                                        {completionResponse &&
                                                            <Box>
                                                                <Typography variant="caption"
                                                                            color="text.secondary"
                                                                            sx={{display: 'block', mb: 1}}>
                                                                    Temp {completionResponse.options.temperature}
                                                                </Typography>
                                                                <Stack spacing={1}>
                                                                    {completionResponse.results.map((result, idx) => (
                                                                        <Paper key={idx}
                                                                               variant="outlined"
                                                                               sx={{
                                                                                   p: 1.5,
                                                                                   whiteSpace: 'pre-wrap',
                                                                                   fontSize: 13,
                                                                               }}>
                                                                            {result}
                                                                        </Paper>
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        }
                                                    </Stack>
                                                }
                                            </Box>
                                        </Collapse>
                                    </Box>
                                }

                                {/* Run on dataset — primary impactful action lives in its own card, not
                                    buried in the footer next to Save. Collapsible header mirrors the
                                    "Try it on a sample row" pattern; expands inline to show the job form. */}
                                {savedTransform &&
                                    <Box sx={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #ececec',
                                        borderRadius: 1,
                                    }}>
                                        <Stack direction="row"
                                               alignItems="center"
                                               justifyContent="space-between"
                                               onClick={() => canRun && setRunExpanded(e => !e)}
                                               sx={{
                                                   px: 2,
                                                   py: 1.5,
                                                   cursor: canRun ? 'pointer' : 'default',
                                                   userSelect: 'none',
                                                   opacity: canRun ? 1 : 0.6,
                                               }}>
                                            <Box sx={{minWidth: 0, flex: 1}}>
                                                <Typography variant="subtitle2">Run on dataset</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {unsavedChanges
                                                        ? "Save your changes to run on the dataset."
                                                        : hasActiveJob
                                                            ? "A job is currently running. Track progress in the Active tasks strip."
                                                            : "Apply this transform to every row and write the result to a column."}
                                                </Typography>
                                            </Box>
                                            {canRun &&
                                                <IconButton size="small" sx={{pointerEvents: 'none'}}>
                                                    {runExpanded
                                                        ? <ExpandLessIcon fontSize="small"/>
                                                        : <ExpandMoreIcon fontSize="small"/>
                                                    }
                                                </IconButton>
                                            }
                                        </Stack>
                                        <Collapse in={runExpanded && canRun} unmountOnExit>
                                            <Box sx={{px: 2, pb: 2}}>
                                                <TransformJobForm project={project}
                                                                  transform={savedTransform}
                                                                  onCancel={() => setRunExpanded(false)}
                                                                  onStarted={handleJobStarted}/>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                }

                                {savedTransform &&
                                    <Box sx={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #ececec',
                                        borderRadius: 1,
                                    }}>
                                        <Stack direction="row"
                                               alignItems="center"
                                               justifyContent="space-between"
                                               onClick={() => savedTransform && setQualityCheckExpanded(e => !e)}
                                               sx={{
                                                   px: 2,
                                                   py: 1.5,
                                                   cursor: savedTransform ? 'pointer' : 'default',
                                                   userSelect: 'none',
                                                   opacity: savedTransform ? 1 : 0.6,
                                               }}>
                                            <Box sx={{minWidth: 0, flex: 1}}>
                                                <Typography variant="subtitle2">Check quality</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Compare outputs against expected values in a column.
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" sx={{pointerEvents: 'none'}}>
                                                {qualityCheckExpanded
                                                    ? <ExpandLessIcon fontSize="small"/>
                                                    : <ExpandMoreIcon fontSize="small"/>
                                                }
                                            </IconButton>
                                        </Stack>
                                        <Collapse in={qualityCheckExpanded} unmountOnExit>
                                            <Box sx={{px: 2, pb: 2}}>
                                                <TransformQualityCheckForm
                                                    project={project}
                                                    transform={savedTransform}
                                                />
                                            </Box>
                                        </Collapse>
                                    </Box>
                                }
                            </Stack>
                        )}
                    </Box>

                    <Divider/>
                    <Stack direction="row"
                           spacing={1}
                           justifyContent="flex-end"
                           sx={{px: 3, py: 2, backgroundColor: '#fff'}}>
                        <Button variant="outlined" color="secondary" onClick={onClose}>
                            {unsavedChanges ? "Cancel" : "Close"}
                        </Button>
                        <AsyncButton variant="contained"
                                     waiting={saving}
                                     disabled={!unsavedChanges || !editingTransform.userPrompt?.trim() || usedReferences.length === 0}
                                     onClick={handleSave}>
                            Save
                        </AsyncButton>
                    </Stack>
                </Box>
            </Modal>

            <CompletionOptionsFormModal open={testSettingsOpen}
                                        setOpen={setTestSettingsOpen}
                                        model={testModel}
                                        options={testCompletionOptions}
                                        updateModel={setTestModel}
                                        updateCompletionOptions={setTestCompletionOptions}/>
        </>
    )
}

export default TransformEditModal
